import React from 'react';
import { toast } from 'sonner';
import { Send, CalendarClock, Loader2 } from 'lucide-react';
import { adminApi } from '../../lib/adminAuth';
import PeoplePicker from './PeoplePicker';
import TemplatePicker from './TemplatePicker';

const GROUPS = [
  { key: 'employee', label: 'Employees' }, { key: 'client', label: 'Clients' }, { key: 'admin', label: 'Admins' },
  { key: 'hr', label: 'HR' }, { key: 'accountant', label: 'Accountants' },
];
const AUDIENCE = [{ key: 'all', label: 'Everyone' }, { key: 'roles', label: 'By group' }, { key: 'users', label: 'Specific people' }];
const inputCls = 'w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a1a2e]/20';

export default function PushComposer({ onSent, templates, onTemplatesChange }) {
  const [f, setF] = React.useState({ title: '', body: '', link: '', audience: 'all', roles: [], recipients: [], schedule: false, when: '', important: false });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleRole = (k) => set('roles', f.roles.includes(k) ? f.roles.filter((r) => r !== k) : [...f.roles, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.schedule && !f.when) return toast.error('Pick a date and time');
    setBusy(true);
    try {
      const payload = { title: f.title, body: f.body, link: f.link, important: f.important, audience: f.audience, roles: f.roles,
        recipients: f.recipients.map(({ user_id, user_type }) => ({ user_id, user_type })),
        scheduled_at: f.schedule ? new Date(f.when).toISOString() : null };
      const r = await adminApi.post('/api/admin/push/campaigns', payload);
      toast.success(r.data.status === 'scheduled' ? 'Notification scheduled' : `Sent to ${r.data.sent} of ${r.data.devices} phones`);
      setF({ title: '', body: '', link: '', audience: 'all', roles: [], recipients: [], schedule: false, when: '', important: false });
      onSent();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send'); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="soft-card p-5 space-y-4" data-testid="push-composer">
      <h2 className="font-semibold text-[#1a1a2e]">New notification</h2>
      <TemplatePicker templates={templates} onPick={(t) => setF((p) => ({ ...p, title: t.title, body: t.body }))} draft={f} onSaved={onTemplatesChange} />
      <input value={f.title} onChange={(e) => set('title', e.target.value)} maxLength={120} placeholder="Title" className={inputCls} data-testid="push-title-input" />
      <textarea value={f.body} onChange={(e) => set('body', e.target.value)} maxLength={500} rows={3} placeholder="Message" className={`${inputCls} h-auto py-2`} data-testid="push-body-input" />
      <input value={f.link} onChange={(e) => set('link', e.target.value)} placeholder="Open screen / link when tapped (optional)" className={inputCls} data-testid="push-link-input" />
      <div className="flex flex-wrap gap-2" data-testid="push-audience-options">
        {AUDIENCE.map((a) => (
          <button type="button" key={a.key} onClick={() => set('audience', a.key)} data-testid={`push-audience-${a.key}`}
            className={`px-4 h-9 rounded-full text-sm font-medium border transition-colors ${f.audience === a.key ? 'bg-[#1a1a2e] text-white border-[#1a1a2e]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{a.label}</button>
        ))}
      </div>
      {f.audience === 'roles' && (
        <div className="flex flex-wrap gap-3" data-testid="push-groups">
          {GROUPS.map((g) => (
            <label key={g.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={f.roles.includes(g.key)} onChange={() => toggleRole(g.key)} data-testid={`push-group-${g.key}`} /> {g.label}
            </label>
          ))}
        </div>
      )}
      {f.audience === 'users' && <PeoplePicker value={f.recipients} onChange={(v) => set('recipients', v)} />}
      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={f.important} onChange={(e) => set('important', e.target.checked)} data-testid="push-important-toggle" />
        <span><span className="font-medium">Important</span> <span className="text-xs text-slate-500">reaches people even if they switched off announcements</span></span>
      </label>
      <p className="text-[11px] text-slate-400">Tip: write {'{name}'} to insert each person&apos;s first name.</p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={f.schedule} onChange={(e) => set('schedule', e.target.checked)} data-testid="push-schedule-toggle" /> Schedule for later
        </label>
        {f.schedule && <input type="datetime-local" value={f.when} onChange={(e) => set('when', e.target.value)} className={`${inputCls} w-auto`} data-testid="push-schedule-input" />}
      </div>
      <button type="submit" disabled={busy || !f.title.trim() || !f.body.trim()} data-testid="push-send-button"
        className="h-10 px-5 rounded-full bg-[#1a1a2e] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-black transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : f.schedule ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
        {f.schedule ? 'Schedule' : 'Send now'}
      </button>
    </form>
  );
}
